import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, SkipForward } from 'lucide-react';
import { useCountdown } from '../../hooks/useCountdown';
import type { Poll, Candidate } from '../../types';

interface VotingInterfaceProps {
  poll: Poll;
  candidate: Candidate | null;
  nextCandidate: Candidate | null;
  currentIndex: number;
  totalCandidates: number;
  previousVote?: boolean;
  isVisible: boolean;
  onVote: (candidateId: string, vote: boolean) => void;
  onKeepVote: () => void;
}

function getAvailableActions(poll: Poll): string[] {
  const actions: string[] = [];
  
  if (poll.settings.allowNominations) {
    actions.push('Nominate your own candidates');
  }
  actions.push('Review and modify your votes');
  
  return actions;
}

export function VotingInterface({
  poll,
  candidate,
  nextCandidate,
  currentIndex,
  totalCandidates,
  previousVote,
  isVisible,
  onVote,
  onKeepVote
}: VotingInterfaceProps) {
  const [selectedVote, setSelectedVote] = useState<'up' | 'down' | null>(null);
  const [animatingVote, setAnimatingVote] = useState<'up' | 'down' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeRemaining = useCountdown(poll.settings.expiresAt);

  const nextCandidatePreviousVote = nextCandidate
    ? poll.votes[Object.keys(poll.votes)[0]]?.[nextCandidate.id]
    : undefined;

  const ANIMATION_DURATION = 300;

  const handleVote = (vote: boolean) => {
    if (!isVisible || isAnimating) return;
    if (!candidate) return;
    
    setIsAnimating(true);
    setSelectedVote(vote ? 'up' : 'down');

    // Animate old vote shrinking if changing votes
    if (previousVote !== undefined && previousVote !== vote) {
      setAnimatingVote(previousVote ? 'up' : 'down');
    }

    // Wait for button animation before triggering vote
    setTimeout(() => {
      onVote(candidate.id, vote);
      setSelectedVote(null);
      setAnimatingVote(null);
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  };

  const handleSkip = () => {
    if (!isVisible || isAnimating) return;
    
    setIsAnimating(true);
    
    setSelectedVote(previousVote ? 'up' : 'down');
    
    // Wait for button animation before skipping
    setTimeout(() => {
      onKeepVote();
      setSelectedVote(null);
      setAnimatingVote(null);
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  };

  const getButtonClasses = (type: 'up' | 'down', isSelected: boolean) => {
    const baseClass = type === 'up' ? 'vote-button-up' : 'vote-button-down';
    const selectedClass = type === 'up' ? 'vote-button-selected-up' : 'vote-button-selected-down';
    
    return `vote-button ${
      isSelected ? selectedClass : baseClass
    } ${
      selectedVote === type ? 'vote-button-pulse' : ''
    } ${
      animatingVote === type ? 'vote-button-shrink' : ''
    }`;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          {poll.name}
        </h2>
        {timeRemaining && (
          <p className="text-gray-600 dark:text-gray-400">
            {timeRemaining}
          </p>
        )}
      </div>

      <div className="voting-container" style={{ height: '280px' }}>
        {candidate && (
          <div className={`voting-card ${!isVisible ? 'sliding' : ''}`} key={candidate.id}>
            <div className="voting-content">
              <div className="text-center flex-1 flex flex-col justify-center">
                <p className="text-gray-400 mb-2">
                  Candidate {currentIndex + 1} of {totalCandidates}
                </p>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {candidate.name}
                </h3>
                {previousVote !== undefined && (
                  <p className="text-gray-400">
                    Your previous vote: {previousVote ? 'Thumbs up' : 'Thumbs down'}
                  </p>
                )}
              </div>

              <div className="voting-buttons">
                <button
                  onClick={() => handleVote(false)}
                  disabled={!isVisible}
                  className={getButtonClasses('down', previousVote === false)}
                  title="Vote No"
                >
                  <ThumbsDown className="w-8 h-8 text-red-400" />
                </button>

                <button
                  onClick={() => handleVote(true)}
                  disabled={!isVisible}
                  className={getButtonClasses('up', previousVote === true)}
                  title="Vote Yes"
                >
                  <ThumbsUp className="w-8 h-8 text-green-400" />
                </button>

                {previousVote !== undefined && (
                  <button
                    onClick={handleSkip}
                    disabled={!isVisible}
                    className="vote-button vote-button-skip"
                    title="Keep previous vote"
                  >
                    <SkipForward className="w-8 h-8 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {nextCandidate && (
          <div className={`voting-card next ${!isVisible ? 'sliding' : ''}`} key={nextCandidate.id}>
            <div className="voting-content">
              <div className="text-center flex-1 flex flex-col justify-center">
                <p className="text-gray-400 mb-2">
                  Candidate {currentIndex + 2} of {totalCandidates}
                </p>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {nextCandidate.name}
                </h3>
                {nextCandidatePreviousVote !== undefined && (
                  <p className="text-gray-400">
                    Your previous vote: {nextCandidatePreviousVote ? 'Thumbs up' : 'Thumbs down'}
                  </p>
                )}
              </div>
              <div className="voting-buttons">
                <button 
                  className={`vote-button ${nextCandidatePreviousVote === false ? 'vote-button-selected-down' : 'vote-button-down'}`} 
                  disabled
                >
                  <ThumbsDown className="w-8 h-8 text-red-400" />
                </button>
                <button 
                  className={`vote-button ${nextCandidatePreviousVote === true ? 'vote-button-selected-up' : 'vote-button-up'}`}
                  disabled
                >
                  <ThumbsUp className="w-8 h-8 text-green-400" />
                </button>
                {nextCandidatePreviousVote !== undefined && (
                  <button
                    className="vote-button vote-button-skip"
                    disabled
                  >
                    <SkipForward className="w-8 h-8 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 text-center text-gray-600 dark:text-gray-400 space-y-2">
        {!poll.settings.singleUpvoteOnly && (
          <p className="text-sm">
            You can upvote more than one candidate
          </p>
        )}
        
        <div>
          <p className="text-sm">
            After voting on all candidates
            {getAvailableActions(poll).length > 1 ? ', you can:' : ' you can '}
          </p>
          {getAvailableActions(poll).length > 1 ? (
            <ul className="list-disc list-inside text-sm">
              {getAvailableActions(poll).map((action, index) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">
              {getAvailableActions(poll)[0].toLowerCase()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}